/* Workspace management (Tier 1): my account (password), members
   (create with one-time password, roles, disable, reset) and API keys
   (create with plaintext-once, revoke). Member/key sections are
   admin/owner only. */
import React, { useEffect, useState } from 'react';
import { I } from '../ui.js';
import { useStore } from '../store.jsx';
import { api, type ApiKeyView, type UserView } from '../api.js';
import { Drawer } from './Drawers.jsx';

function ChangePassword() {
  const { toast } = useStore();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (next.length < 8) { toast('New password needs at least 8 characters'); return; }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent(''); setNext('');
      toast('Password changed — other sessions were signed out');
    } catch (err) { toast((err as Error).message); }
    setBusy(false);
  };
  return (
    <>
      <div className="section-h">My account</div>
      <div className="iv-custom-add">
        <input className="input sm" type="password" placeholder="Current password" value={current} onChange={e => setCurrent(e.target.value)} />
        <input className="input sm" type="password" placeholder="New password (min 8)" value={next} onChange={e => setNext(e.target.value)} />
        <button className="btn sm primary" disabled={busy} onClick={() => void go()}>Change</button>
      </div>
    </>
  );
}

function OneTimeSecret({ label, value, onDismiss }: { label: string; value: string; onDismiss: () => void }) {
  return (
    <div className="callout warn" style={{ marginTop: 10 }}>
      <div className="ico"><I k="eye" /></div>
      <div>
        <strong>{label}</strong> — shown exactly once, hand it over out-of-band:
        <div className="mono" style={{ fontSize: 14, fontWeight: 700, margin: '6px 0' }}>{value}</div>
        <button className="btn sm" onClick={onDismiss}>I stored it</button>
      </div>
    </div>
  );
}

function Members() {
  const { s, toast } = useStore();
  const [users, setUsers] = useState<UserView[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('strategist');
  const [secret, setSecret] = useState<{ label: string; value: string } | null>(null);
  const myRole = s.user?.role ?? 'viewer';
  const grantable = myRole === 'owner' ? ['viewer', 'strategist', 'admin']
    : myRole === 'admin' ? ['viewer', 'strategist', 'admin'] : [];

  const load = () => void api.users().then(r => setUsers(r.users)).catch(() => setUsers([]));
  useEffect(load, []);

  const create = async () => {
    try {
      const res = await api.createUser({ email: email.trim(), role });
      setSecret({ label: `One-time password for ${res.user.email}`, value: res.oneTimePassword });
      setEmail('');
      load();
    } catch (err) { toast((err as Error).message); }
  };
  const act = async (fn: () => Promise<unknown>, msg?: string) => {
    try { await fn(); load(); if (msg) toast(msg); }
    catch (err) { toast((err as Error).message); }
  };

  return (
    <>
      <div className="section-h">Members</div>
      {users.map(u => (
        <div className="mgl-row" key={u.id} style={{ opacity: u.disabled ? 0.55 : 1 }}>
          <div className="nm" style={{ whiteSpace: 'normal' }}>
            <strong>{u.name}</strong> · {u.email}
            {u.disabled && <span className="iv-badge" style={{ marginLeft: 6 }}>disabled</span>}
            {u.id === s.user?.id && <span className="iv-badge lib" style={{ marginLeft: 6 }}>you</span>}
          </div>
          {u.role === 'owner' || u.id === s.user?.id
            ? <span className="prov">{u.role}</span>
            : (
              <select className="select sm" value={u.role}
                onChange={e => void act(() => api.updateUser(u.id, { role: e.target.value }), 'Role updated')}>
                {grantable.map(r => <option key={r}>{r}</option>)}
              </select>
            )}
          {u.id !== s.user?.id && u.role !== 'owner' && (
            <>
              <button className="btn sm" title="Issue a new one-time password"
                onClick={() => void act(async () => {
                  const r = await api.resetUserPassword(u.id);
                  setSecret({ label: `New one-time password for ${u.email}`, value: r.oneTimePassword });
                })}>reset</button>
              <button className="btn sm" onClick={() => void act(
                () => (u.disabled ? api.enableUser(u.id) : api.disableUser(u.id)),
                u.disabled ? 'Member enabled' : 'Member disabled — sessions revoked',
              )}>{u.disabled ? 'enable' : 'disable'}</button>
            </>
          )}
        </div>
      ))}
      <div className="iv-custom-add" style={{ marginTop: 10 }}>
        <input className="input sm" placeholder="colleague@company.com" value={email} onChange={e => setEmail(e.target.value)} />
        <select className="select sm" value={role} onChange={e => setRole(e.target.value)}>
          {grantable.map(r => <option key={r}>{r}</option>)}
        </select>
        <button className="btn sm primary" disabled={!email.includes('@')} onClick={() => void create()}>
          <I k="plus" /> Add
        </button>
      </div>
      {secret && <OneTimeSecret label={secret.label} value={secret.value} onDismiss={() => setSecret(null)} />}
    </>
  );
}

function ApiKeys() {
  const { toast } = useStore();
  const [keys, setKeys] = useState<ApiKeyView[]>([]);
  const [name, setName] = useState('');
  const [role, setRole] = useState('strategist');
  const [secret, setSecret] = useState<string | null>(null);

  const load = () => void api.keys().then(r => setKeys(r.keys)).catch(() => setKeys([]));
  useEffect(load, []);

  const create = async () => {
    try {
      const res = await api.createKey({ name: name.trim() || 'unnamed', role });
      setSecret(res.key);
      setName('');
      load();
    } catch (err) { toast((err as Error).message); }
  };

  return (
    <>
      <div className="section-h">API keys <span style={{ textTransform: 'none', color: 'var(--ink-3)', fontWeight: 400 }}>— for the public API (`Authorization: Bearer …`)</span></div>
      {keys.map(k => (
        <div className="mgl-row" key={k.id} style={{ opacity: k.revoked ? 0.55 : 1 }}>
          <div className="nm" style={{ whiteSpace: 'normal' }}>
            <strong>{k.name}</strong> <span className="mono">{k.prefix}…</span>
            <span className="prov" style={{ marginLeft: 6 }}>{k.role}</span>
            {k.revoked && <span className="iv-badge" style={{ marginLeft: 6 }}>revoked</span>}
            <div className="note">created {new Date(k.createdAt).toLocaleDateString()} by {k.createdBy}
              {k.lastUsedAt && <> · last used {new Date(k.lastUsedAt).toLocaleDateString()}</>}</div>
          </div>
          {!k.revoked && (
            <button className="btn sm" onClick={() => void api.revokeKey(k.id).then(load)}>revoke</button>
          )}
        </div>
      ))}
      <div className="iv-custom-add" style={{ marginTop: 10 }}>
        <input className="input sm" placeholder="key name — e.g. ci-bot" value={name} onChange={e => setName(e.target.value)} />
        <select className="select sm" value={role} onChange={e => setRole(e.target.value)}>
          <option>strategist</option><option>viewer</option>
        </select>
        <button className="btn sm primary" onClick={() => void create()}><I k="plus" /> Mint</button>
      </div>
      {secret && <OneTimeSecret label="API key" value={secret} onDismiss={() => setSecret(null)} />}
    </>
  );
}

export function WorkspaceDrawer() {
  const { s, set } = useStore();
  const canManage = s.user?.role === 'admin' || s.user?.role === 'owner';
  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<><I k="owner" /> Workspace</>} title="Account & workspace"
      foot={<><div className="muted" style={{ fontSize: 11.5 }}>Every change here is audited.</div><div className="grow" /><button className="btn primary" onClick={() => set({ drawer: null })}>Done</button></>}>
      <ChangePassword />
      {canManage ? <><Members /><ApiKeys /></> : (
        <div className="callout info" style={{ marginTop: 14 }}>
          <div className="ico"><I k="info" /></div>
          <div>Member and API-key management is available to admins and owners.</div>
        </div>
      )}
    </Drawer>
  );
}
